import { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  CircularProgress,
  Divider,
  Chip,
  InputAdornment,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BadgeIcon from "@mui/icons-material/Badge";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { useApi } from "../../hooks/useApi";
import { formatPhoneNumber } from "../../utils/functions";
import UsPhoneField, { toPhoneDigits, toE164 } from "../shared/UsPhoneField";

dayjs.extend(utc);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "14px",
      color: "#111008",
      fontFamily: "inherit",
      "::placeholder": { color: "#b0b0b0" },
    },
    invalid: { color: "#e53935" },
  },
};

// ─── constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  client_user_id: null,
  client_mode: "search",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  barber_user_id: null,
  service_id: null,
  date: dayjs().format("YYYY-MM-DD"),
  starts_at: null,
  notes: "",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 14 },
};

// ─── Inner component (must be inside <Elements>) ──────────────────────────────

function NewBookingDrawerInner({
  open,
  onClose,
  barbers,
  onCreated,
  preselectedBarber = null,
  preselectedDate = null,
  preselectedSlot = null,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const api = useApi();

  const [form, setForm] = useState(EMPTY_FORM);
  const [clientSearch, setClientSearch] = useState("");
  const [allClients, setAllClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState("cash"); // "cash" | "card"
  const [cardMode, setCardMode] = useState("new"); // "saved" | "new"
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false); // true when CardElement is filled

  // ── Reset when drawer opens ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    setForm({
      ...EMPTY_FORM,
      barber_user_id: preselectedBarber?.user_id || null,
      date: preselectedDate || dayjs().format("YYYY-MM-DD"),
      starts_at: preselectedSlot?.start_utc || null,
    });
    setClientSearch("");
    setError(null);
    setSlots([]);
    setPaymentMethod("cash");
    setCardMode("new");
    setSelectedPaymentMethodId(null);
    setSavedCards([]);
    setCardComplete(false);

    if (!clientsLoaded) loadAllClients();
  }, [open]);

  // ── Load all clients ──────────────────────────────────────────────────
  const loadAllClients = async () => {
    setClientsLoading(true);
    try {
      const res = await api("/tenants/clients.php", { method: "GET" });
      setAllClients(res.data.clients || []);
      setClientsLoaded(true);
    } catch {
      setAllClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  // ── Fetch saved cards whenever a client is selected ──────────────────
  // Does NOT auto-switch paymentMethod — cash stays cash. If a card exists
  // it is pre-selected as protection; the warning only shows when there are none.
  const fetchSavedCards = useCallback(async (clientId) => {
    setCardsLoading(true);
    setSavedCards([]);
    setSelectedPaymentMethodId(null);
    setCardMode("new");
    try {
      const res = await api(
        `/stripe/payment-methods.php?client_user_id=${clientId}`,
        { method: "GET" },
      );
      const cards = res.data.payment_methods || [];
      setSavedCards(cards);
      if (cards.length > 0) {
        setCardMode("saved");
        setSelectedPaymentMethodId(cards[0].id);
      }
    } catch {
      setSavedCards([]);
    } finally {
      setCardsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (form.client_mode === "search" && form.client_user_id) {
      fetchSavedCards(form.client_user_id);
    } else {
      setSavedCards([]);
      setSelectedPaymentMethodId(null);
      setCardMode("new");
      setPaymentMethod("cash");
    }
  }, [form.client_user_id, form.client_mode]);

  // ── Filter clients locally ────────────────────────────────────────────
  const filteredClients = useMemo(() => {
    if (clientSearch.trim().length < 2) return [];
    const query = clientSearch.toLowerCase().trim();
    const queryDigits = query.replace(/\D/g, "");

    return allClients
      .filter((client) => {
        const matchesText =
          `${client.first_name} ${client.last_name} ${client.email}`
            .toLowerCase()
            .includes(query);
        const phoneDigits = (client.phone_number || "").replace(/\D/g, "");
        const matchesPhone =
          queryDigits.length >= 3 && phoneDigits.includes(queryDigits);
        return matchesText || matchesPhone;
      })
      .slice(0, 8);
  }, [clientSearch, allClients]);

  // ── Load services when barber changes ────────────────────────────────
  useEffect(() => {
    if (!form.barber_user_id) {
      setServices([]);
      return;
    }
    api(`/barbers/services.php?barber_id=${form.barber_user_id}`, {
      method: "GET",
    })
      .then((res) =>
        setServices(
          (res.data.services || []).filter((s) => s.is_active && !s.is_deleted),
        ),
      )
      .catch(() => setServices([]));

    setForm((f) => ({
      ...f,
      service_id: null,
      date: preselectedDate || f.date,
      starts_at: preselectedSlot?.start_utc || null,
    }));
    setSlots([]);
  }, [form.barber_user_id]);

  // ── Load availability slots ───────────────────────────────────────────
  const loadSlots = useCallback(
    async (barber_id, service_id, date) => {
      if (!barber_id || !service_id || !date) return;
      setSlotsLoading(true);
      setSlots([]);
      setForm((f) => ({ ...f, starts_at: null }));
      try {
        const res = await api(
          `/bookings/availability.php?barber_id=${barber_id}&service_id=${service_id}&date=${date}`,
          { method: "GET" },
        );
        if (res.data.available) {
          setSlots(res.data.slots || []);
          if (preselectedSlot) {
            const match = (res.data.slots || []).find(
              (s) => s.start_utc === preselectedSlot.start_utc,
            );
            if (match) setForm((f) => ({ ...f, starts_at: match.start_utc }));
          }
        } else {
          setSlots([]);
          setError(res.data.reason || "No availability on this date.");
        }
      } catch {
        setSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    },
    [preselectedSlot],
  );

  useEffect(() => {
    if (form.barber_user_id && form.service_id && form.date) {
      setError(null);
      loadSlots(form.barber_user_id, form.service_id, form.date);
    }
  }, [form.barber_user_id, form.service_id, form.date]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const selectedBarber = barbers.find((b) => b.user_id === form.barber_user_id);
  const selectedService = services.find(
    (s) => s.service_id === form.service_id,
  );

  const isClientReady =
    form.client_mode === "search"
      ? !!form.client_user_id
      : form.first_name.trim() && form.phone.trim();

  const isPaymentReady =
    paymentMethod === "cash" || // cash is always bookable — card is optional protection
    (paymentMethod === "card" &&
      cardMode === "saved" &&
      !!selectedPaymentMethodId) ||
    (paymentMethod === "card" && cardMode === "new" && cardComplete);

  const isReady =
    isClientReady &&
    form.barber_user_id &&
    form.service_id &&
    form.starts_at &&
    isPaymentReady;

  // card available for existing clients (search) and manual guests (name+phone filled)
  const canUseCard =
    form.client_mode === "search" ? !!form.client_user_id : isClientReady;

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        barber_user_id: form.barber_user_id,
        service_id: form.service_id,
        starts_at: form.starts_at,
        notes: form.notes || null,
      };

      if (form.client_mode === "search" && form.client_user_id) {
        body.client_user_id = form.client_user_id;
      } else {
        body.client_first_name = form.first_name.trim();
        body.client_last_name = form.last_name.trim();
        body.client_email = form.email.trim();
        body.client_phone = form.phone.trim();
      }

      if (paymentMethod === "cash") {
        // hasCard is true only when a saved card is selected OR
        // the new card element has been fully filled in
        const hasCard =
          (cardMode === "saved" && !!selectedPaymentMethodId) ||
          (cardMode === "new" && cardComplete);

        if (hasCard) {
          // ── Cash + card → SetupIntent (saves card for no-show/cancel protection)
          let pmId;
          if (cardMode === "saved") {
            pmId = selectedPaymentMethodId;
          } else {
            const cardElement = elements.getElement(CardElement);
            const { error: stripeError, paymentMethod: pm } =
              await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
              });
            if (stripeError) {
              setError(stripeError.message);
              setSubmitting(false);
              return;
            }
            pmId = pm.id;
          }

          const res = await api("/stripe/create-setup-intent.php", {
            method: "POST",
            body: JSON.stringify({ ...body, payment_method_id: pmId }),
          });

          if (res?.meta?.code === 200) {
            onCreated?.();
            onClose();
          }
        } else {
          // ── Cash + no card → appointments.php (admin-only, no Stripe)
          const res = await api("/tenants/appointments.php", {
            method: "POST",
            body: JSON.stringify(body),
          });

          if (res?.meta?.code === 200) {
            onCreated?.();
            onClose();
          }
        }
      } else {
        // ── Card payment → PaymentIntent (charges client immediately)
        let pmId;
        if (cardMode === "saved") {
          pmId = selectedPaymentMethodId;
        } else {
          const cardElement = elements.getElement(CardElement);
          const { error: stripeError, paymentMethod: pm } =
            await stripe.createPaymentMethod({
              type: "card",
              card: cardElement,
            });
          if (stripeError) {
            setError(stripeError.message);
            setSubmitting(false);
            return;
          }
          pmId = pm.id;
        }

        const res = await api("/stripe/create-payment-intent.php", {
          method: "POST",
          body: JSON.stringify({ ...body, payment_method_id: pmId }),
        });

        if (res?.meta?.code === 200) {
          onCreated?.();
          onClose();
        }
      }
    } catch (err) {
      // 409 = the slot was taken between loading availability and submitting.
      // Clear the now-stale selection and refresh the slot list so the user
      // can pick an open time instead of re-submitting the same dead slot.
      if (err?.status === 409) {
        setError(
          err?.message ||
            "That time was just booked by someone else. Please pick another slot.",
        );
        set("starts_at", null);
        if (form.barber_user_id && form.service_id && form.date) {
          loadSlots(form.barber_user_id, form.service_id, form.date);
        }
      } else {
        setError(err?.message || "Failed to create booking.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: 500 },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">New Booking</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: 1.5, fontSize: 13 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* ── CLIENT ───────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            mb: 1,
          }}
        >
          <PersonIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Client
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, mb: 1.5 }}>
          {["search", "manual"].map((mode) => (
            <Chip
              key={mode}
              label={mode === "search" ? "Search existing" : "Enter manually"}
              onClick={() => {
                set("client_mode", mode);
                set("client_user_id", null);
                setClientSearch("");
                setPaymentMethod("cash");
              }}
              size="small"
              sx={{
                backgroundColor:
                  form.client_mode === mode ? "#1a1a1a" : "white",
                color: form.client_mode === mode ? "white" : "#1a1a1a",
                border: "1px solid",
                borderColor: form.client_mode === mode ? "#1a1a1a" : "#e5e5e5",
                fontWeight: 500,
                fontSize: 12,
                "&:hover": {
                  backgroundColor:
                    form.client_mode === mode ? "#000" : "#f5f5f5",
                },
              }}
            />
          ))}
        </Box>

        {form.client_mode === "search" ? (
          <Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email or phone..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              sx={INPUT_SX}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      {clientsLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <SearchIcon sx={{ fontSize: 16, color: "#9b9b9b" }} />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />

            {filteredClients.length > 0 && (
              <Box
                sx={{
                  mt: 0.5,
                  border: "1px solid #f0f0f0",
                  borderRadius: 1.5,
                  backgroundColor: "white",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                {filteredClients.map((client, i) => (
                  <Box
                    key={client.user_id}
                    onClick={() => {
                      set("client_user_id", client.user_id);
                      setClientSearch("");
                    }}
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderTop: i > 0 ? "1px solid #f5f5f5" : "none",
                      cursor: "pointer",
                      backgroundColor:
                        form.client_user_id === client.user_id
                          ? "#f9f9f9"
                          : "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {client.first_name} {client.last_name}
                      </Typography>
                      <Typography variant="caption">{client.email}</Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      <a
                        href={`tel:${client.phone_number}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {formatPhoneNumber(client.phone_number)}
                      </a>
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {form.client_user_id && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 1,
                  p: 1.5,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1.5,
                  border: "1px solid #f0f0f0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#1D9E75",
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {
                      allClients.find((c) => c.user_id === form.client_user_id)
                        ?.first_name
                    }{" "}
                    {
                      allClients.find((c) => c.user_id === form.client_user_id)
                        ?.last_name
                    }
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => {
                    set("client_user_id", null);
                    setClientSearch("");
                    setPaymentMethod("cash");
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    color: "#9b9b9b",
                    minWidth: 0,
                  }}
                >
                  Change
                </Button>
              </Box>
            )}

            {clientSearch.length >= 2 &&
              filteredClients.length === 0 &&
              !clientsLoading &&
              !form.client_user_id && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No clients found.{" "}
                  <Box
                    component="span"
                    onClick={() => set("client_mode", "manual")}
                    sx={{
                      color: "#9c6a36",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Enter manually
                  </Box>
                </Typography>
              )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1.5 }}>
              <TextField
                label="First name"
                size="small"
                fullWidth
                sx={INPUT_SX}
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
              />
              <TextField
                label="Last name"
                size="small"
                fullWidth
                sx={INPUT_SX}
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
              />
            </Box>
            <UsPhoneField
              value={toPhoneDigits(form.phone)}
              onChange={(digits) => set("phone", toE164(digits))}
            />
            <TextField
              label="Email (optional)"
              size="small"
              fullWidth
              sx={INPUT_SX}
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* ── BARBER ───────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            mb: 1.5,
          }}
        >
          <BadgeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Barber
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 1,
            mb: 0.5,
          }}
        >
          {barbers.map((barber) => {
            const sel = form.barber_user_id === barber.user_id;
            return (
              <Chip
                key={barber.user_id}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: barber.color.dot,
                      }}
                    />
                    <span>
                      {barber.first_name} {barber.last_name}
                    </span>
                  </Box>
                }
                onClick={() => set("barber_user_id", barber.user_id)}
                sx={{
                  backgroundColor: sel ? barber.color.bg : "white",
                  border: `1px solid ${sel ? barber.color.border : "#e5e5e5"}`,
                  color: sel ? barber.color.text : "#1a1a1a",
                  fontWeight: 500,
                  fontSize: 13,
                  "& .MuiChip-label": { px: 1.25 },
                  "&:hover": {
                    backgroundColor: barber.color.bg,
                    borderColor: barber.color.border,
                  },
                }}
              />
            );
          })}
        </Box>

        {/* ── SERVICE ──────────────────────────────────────────── */}
        {form.barber_user_id && (
          <>
            <Divider sx={{ my: 2.5 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <ContentCutIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Service
              </Typography>
            </Box>
            {services.length === 0 ? (
              <Typography variant="body2">No services available.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {services.map((service) => {
                  const sel = form.service_id === service.service_id;
                  return (
                    <Box
                      key={service.service_id}
                      onClick={() => set("service_id", service.service_id)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: sel ? "#1a1a1a" : "#f0f0f0",
                        backgroundColor: sel ? "#1a1a1a" : "white",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: "#1a1a1a" },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: sel ? "white" : "text.primary",
                          }}
                        >
                          {service.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: sel
                              ? "rgba(255,255,255,0.6)"
                              : "text.secondary",
                            mt: 0.25,
                            display: "block",
                          }}
                        >
                          {service.duration_minutes} min
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: sel ? "white" : "text.primary",
                        }}
                      >
                        ${parseFloat(service.price).toFixed(2)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {/* ── DATE ─────────────────────────────────────────────── */}
        {form.service_id && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <CalendarTodayIcon
                sx={{ fontSize: 15, color: "text.secondary" }}
              />
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Date
              </Typography>
            </Box>
            <TextField
              type="date"
              size="small"
              fullWidth
              sx={INPUT_SX}
              value={form.date}
              slotProps={{ htmlInput: { min: dayjs().format("YYYY-MM-DD") } }}
              onChange={(e) => {
                set("date", e.target.value);
                set("starts_at", null);
                setSlots([]);
              }}
            />
          </>
        )}

        {/* ── TIME SLOTS ───────────────────────────────────────── */}
        {form.date && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Time
              </Typography>
            </Box>

            {slotsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2">Loading availability...</Typography>
              </Box>
            ) : slots.length === 0 ? (
              <Typography variant="body2">
                No available slots on this date.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                {slots.map((slot) => {
                  const sel = form.starts_at === slot.start_utc;
                  return (
                    <Box
                      key={slot.start_utc}
                      onClick={() => set("starts_at", slot.start_utc)}
                      sx={{
                        py: 1,
                        textAlign: "center",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: sel ? "#1a1a1a" : "#f0f0f0",
                        backgroundColor: sel ? "#1a1a1a" : "white",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: "#1a1a1a" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: sel ? "white" : "text.primary",
                        }}
                      >
                        {dayjs(`2000-01-01 ${slot.start}`).format("h:mm A")}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {/* ── PAYMENT METHOD ───────────────────────────────────── */}
        {form.starts_at && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <CreditCardIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Payment Method
              </Typography>
            </Box>

            {/* Cash | Card chips */}
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              {[
                { value: "cash", label: "Cash" },
                { value: "card", label: "Card", hidden: !canUseCard },
              ].map(({ value, label, hidden }) => {
                if (hidden) return null;
                const sel = paymentMethod === value;
                return (
                  <Chip
                    key={value}
                    label={label}
                    onClick={() => setPaymentMethod(value)}
                    size="small"
                    sx={{
                      backgroundColor: sel ? "#1a1a1a" : "white",
                      color: sel ? "white" : "#1a1a1a",
                      border: "1px solid",
                      borderColor: sel ? "#1a1a1a" : "#e5e5e5",
                      fontWeight: 500,
                      fontSize: 12,
                      "&:hover": { backgroundColor: sel ? "#000" : "#f5f5f5" },
                    }}
                  />
                );
              })}
            </Box>

            {/* ── Cash section ── */}
            {paymentMethod === "cash" && (
              <>
                {cardsLoading && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="body2">
                      Loading saved cards...
                    </Typography>
                  </Box>
                )}

                {/* Client has a card on file — show it as protection */}
                {!cardsLoading && savedCards.length > 0 && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "text.secondary",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        mb: 1,
                      }}
                    >
                      Card on file (for no-show protection)
                    </Typography>

                    <Alert
                      severity="info"
                      sx={{
                        mb: 1.5,
                        fontSize: 12,
                        borderRadius: 1.5,
                        backgroundColor: "rgba(196,152,77,0.08)",
                        color: "#111",
                        border: "1px solid rgba(196,152,77,0.2)",
                        "& .MuiAlert-icon": { color: "#C4984D" },
                      }}
                    >
                      Card won't be charged now. Saved only to protect against
                      no-shows or late cancellations.
                    </Alert>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {savedCards.map((card) => {
                        const sel =
                          cardMode === "saved" &&
                          selectedPaymentMethodId === card.id;
                        return (
                          <Box
                            key={card.id}
                            onClick={() => {
                              setCardMode("saved");
                              setSelectedPaymentMethodId(card.id);
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: sel ? "#1a1a1a" : "#f0f0f0",
                              backgroundColor: sel ? "#1a1a1a" : "white",
                              cursor: "pointer",
                              transition: "all 0.15s",
                              "&:hover": { borderColor: "#1a1a1a" },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CreditCardIcon
                                sx={{
                                  fontSize: 16,
                                  color: sel
                                    ? "rgba(255,255,255,0.7)"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: sel ? "white" : "text.primary",
                                  textTransform: "capitalize",
                                }}
                              >
                                {card.brand} •••• {card.last4}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: sel
                                  ? "rgba(255,255,255,0.6)"
                                  : "text.secondary",
                              }}
                            >
                              {card.exp_month}/{String(card.exp_year).slice(-2)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {/* No card on file — warn but allow booking */}
                {!cardsLoading && savedCards.length === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: "#FAEEDA",
                      border: "1px solid #EF9F27",
                    }}
                  >
                    <WarningAmberIcon
                      sx={{
                        fontSize: 16,
                        color: "#854F0B",
                        mt: "1px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 12, color: "#854F0B", lineHeight: 1.5 }}
                    >
                      No card on file — no-show and cancellation fees cannot be
                      charged automatically.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* ── Card section — charge upfront ── */}
            {paymentMethod === "card" && (
              <>
                {cardsLoading && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="body2">
                      Loading saved cards...
                    </Typography>
                  </Box>
                )}

                {!cardsLoading && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {savedCards.map((card) => {
                      const sel =
                        cardMode === "saved" &&
                        selectedPaymentMethodId === card.id;
                      return (
                        <Box
                          key={card.id}
                          onClick={() => {
                            setCardMode("saved");
                            setSelectedPaymentMethodId(card.id);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: sel ? "#1a1a1a" : "#f0f0f0",
                            backgroundColor: sel ? "#1a1a1a" : "white",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            "&:hover": { borderColor: "#1a1a1a" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CreditCardIcon
                              sx={{
                                fontSize: 16,
                                color: sel
                                  ? "rgba(255,255,255,0.7)"
                                  : "text.secondary",
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: sel ? "white" : "text.primary",
                                textTransform: "capitalize",
                              }}
                            >
                              {card.brand} •••• {card.last4}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: sel
                                ? "rgba(255,255,255,0.6)"
                                : "text.secondary",
                            }}
                          >
                            {card.exp_month}/{String(card.exp_year).slice(-2)}
                          </Typography>
                        </Box>
                      );
                    })}

                    {savedCards.length > 0 && (
                      <Box
                        onClick={() => setCardMode("new")}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor:
                            cardMode === "new" ? "#1a1a1a" : "#f0f0f0",
                          backgroundColor:
                            cardMode === "new" ? "rgba(0,0,0,0.03)" : "white",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          "&:hover": { borderColor: "#1a1a1a" },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          + Use a different card
                        </Typography>
                      </Box>
                    )}

                    {cardMode === "new" && (
                      <Box
                        sx={{
                          border: "1px solid #d8d1c7",
                          borderRadius: 1.5,
                          p: 1.5,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <CardElement
                          options={CARD_ELEMENT_OPTIONS}
                          onChange={(e) => setCardComplete(e.complete)}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* ── NOTES ────────────────────────────────────────────── */}
        {form.starts_at && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="overline"
              sx={{ mb: 1.5, display: "block", color: "text.secondary" }}
            >
              Notes (optional)
            </Typography>
            <TextField
              multiline
              rows={3}
              size="small"
              fullWidth
              sx={INPUT_SX}
              placeholder="Any notes for the barber..."
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
          flexShrink: 0,
        }}
      >
        {/* Summary card */}
        {(selectedBarber || selectedService || form.starts_at) && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: "white",
              borderRadius: 1.5,
              border: "1px solid #f0f0f0",
            }}
          >
            {selectedBarber && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption">Barber</Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {selectedBarber.first_name} {selectedBarber.last_name}
                </Typography>
              </Box>
            )}
            {selectedService && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption">Service</Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {selectedService.name} — $
                  {parseFloat(selectedService.price).toFixed(2)}
                </Typography>
              </Box>
            )}
            {form.starts_at && form.date && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption">Time</Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {dayjs(form.date).format("MMM D")} at{" "}
                  {(() => {
                    const raw =
                      slots.find((s) => s.start_utc === form.starts_at)
                        ?.start ||
                      preselectedSlot?.start ||
                      "";
                    return raw
                      ? dayjs(`2000-01-01 ${raw}`).format("h:mm A")
                      : "";
                  })()}
                </Typography>
              </Box>
            )}
            {form.starts_at && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="caption">Payment</Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {paymentMethod === "card"
                    ? cardMode === "saved" && selectedPaymentMethodId
                      ? (() => {
                          const c = savedCards.find(
                            (c) => c.id === selectedPaymentMethodId,
                          );
                          return c
                            ? `${c.brand.charAt(0).toUpperCase() + c.brand.slice(1)} •••• ${c.last4}`
                            : "Card on file";
                        })()
                      : "New card"
                    : "Cash"}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            fullWidth
            sx={{ textTransform: "none", color: "#6b6b6b", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isReady || submitting}
            fullWidth
            sx={{
              backgroundColor: isReady ? "#9c6a36" : "#e5e5e5",
              color: isReady ? "white" : "#9b9b9b",
              textTransform: "none",
              fontWeight: 600,
              py: 1.25,
              borderRadius: 1.5,
              "&:hover": { backgroundColor: isReady ? "#7d5429" : "#e5e5e5" },
            }}
          >
            {submitting ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={14} sx={{ color: "white" }} />
                <span>Creating...</span>
              </Box>
            ) : (
              "Confirm Booking"
            )}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// ─── Outer wrapper — provides Stripe Elements context ────────────────────────
export default function NewBookingDrawer(props) {
  return (
    <Elements stripe={stripePromise}>
      <NewBookingDrawerInner {...props} />
    </Elements>
  );
}
